import type { ApiError, CatalogueItemResource, OutletResource } from '@repo/api-client';

import { messageForFailure } from '@/api/failure';

/**
 * What to say when a barcode is refused because another item already carries
 * it (`CATALOGUE_BARCODE_TAKEN`), naming that item and where it sells.
 *
 * blossom-rest-api #535 puts the item in `details` — `catalogueItemId`, `name`
 * and `outletId`, the lowest outlet id when it clashes at several. The outlet's
 * name comes from the list the screen already holds; one it does not know (not
 * loaded yet, or retired since) leaves the outlet out rather than the sentence.
 *
 * **These details are safe to show only because every route that raises this —
 * create, update, restore and replace availability — is OWNER-only today.** The
 * owner may see every item at every outlet; the API makes no promise that the
 * details are fit for anyone else. Do not lift this into a component for any
 * role, and if a route raising this is ever opened to another role, revisit
 * this rather than reuse it.
 *
 * Anything it cannot read falls back to the generic copy in `error-messages.ts`.
 */
export function barcodeRefusal(
  refusal: ApiError,
  outlets: readonly OutletResource[],
  subject = 'That barcode',
): string {
  const name = refusal.details?.['name'];
  if (typeof name !== 'string' || name.trim() === '') return messageForFailure(refusal);

  const outletId = refusal.details?.['outletId'];
  const outlet = outlets.find((candidate) => candidate.id === outletId);

  return `${subject} is already on ${name}${outlet === undefined ? '' : ` at ${outlet.name}`}.`;
}

/** The subject for a screen with no barcode field: the number, said out loud. */
export const barcodeSubject = (item: CatalogueItemResource): string =>
  item.barcode === null ? 'That barcode' : `The barcode ${item.barcode}`;
